import React, { useState, useEffect } from 'react';
import { Upload, Search, Grid, List, FileText, Image, FileSpreadsheet, Download, Trash2, X, FolderOpen } from 'lucide-react';
import { fetchDocuments, uploadDocument, downloadDocument, deleteDocument } from '../utils/supabaseHelpers';
import { toast } from 'sonner';

const Documents = () => {
  const [documents, setDocuments] = useState([]);
  const [filteredDocuments, setFilteredDocuments] = useState([]);
  const [viewMode, setViewMode] = useState('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Fetch real documents from Supabase
    const loadDocuments = async () => {
      try {
        setLoading(true);
        const docs = await fetchDocuments();
        setDocuments(docs);
      } catch (error) {
        console.error('Failed to load documents:', error);
        toast.error('Failed to load documents: ' + error.message);
      } finally {
        setLoading(false);
      }
    };
    
    loadDocuments();
  }, []);

  useEffect(() => {
    let filtered = documents;

    if (searchTerm) {
      filtered = filtered.filter(d =>
        d.filename.toLowerCase().includes(searchTerm.toLowerCase()) ||
        d.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (categoryFilter !== 'all') {
      filtered = filtered.filter(d => d.category === categoryFilter);
    }

    setFilteredDocuments(filtered);
  }, [documents, searchTerm, categoryFilter]);

  const getFileIcon = (fileType) => {
    switch (fileType) {
      case 'pdf':
        return <FileText className="text-red-400" size={32} />;
      case 'image':
        return <Image className="text-blue-400" size={32} />;
      case 'excel':
        return <FileSpreadsheet className="text-green-400" size={32} />;
      case 'word':
        return <FileText className="text-blue-400" size={32} />;
      default:
        return <FileText className="text-gray-400" size={32} />;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handleDelete = async (doc) => {
    if (window.confirm('Are you sure you want to delete this document?')) {
      try {
        await deleteDocument(doc.id, doc.file_path);
        const updated = documents.filter(d => d.id !== doc.id);
        setDocuments(updated);
        toast.success('Document deleted successfully');
      } catch (error) {
        console.error('Failed to delete document:', error);
        toast.error('Failed to delete document: ' + error.message);
      }
    }
  };

  const handleDownload = async (doc) => {
    try {
      await downloadDocument(doc.file_path, doc.filename);
      toast.success('Download started');
    } catch (error) {
      console.error('Failed to download document:', error);
      toast.error('Failed to download document: ' + error.message);
    }
  };

  return (
    <div className="p-8" data-testid="documents-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold">Document Library</h1>
        <div className="flex gap-3">
          <div className="flex gap-2 glass-card p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded transition-colors ${
                viewMode === 'grid' ? 'bg-blue-500 text-white' : 'text-gray-400 hover:text-white'
              }`}
              data-testid="view-grid-btn"
            >
              <Grid size={20} />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded transition-colors ${
                viewMode === 'list' ? 'bg-blue-500 text-white' : 'text-gray-400 hover:text-white'
              }`}
              data-testid="view-list-btn"
            >
              <List size={20} />
            </button>
          </div>
          <button
            onClick={() => setShowUploadModal(true)}
            className="btn-primary flex items-center gap-2"
            data-testid="upload-btn"
          >
            <Upload size={20} />
            Upload
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="glass-card p-6 mb-8">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search documents..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full glass-input pl-10"
              data-testid="search-documents-input"
            />
          </div>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="glass-input cursor-pointer"
            data-testid="category-filter"
          >
            <option value="all">All Categories</option>
            <option value="datasheets">Datasheets</option>
            <option value="bills">Bills</option>
            <option value="technical">Technical</option>
            <option value="reports">Reports</option>
            <option value="other">Other</option>
          </select>
        </div>
      </div>

      {/* Documents Display */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="loading-dots mb-4">
              <span></span>
              <span></span>
              <span></span>
            </div>
            <p className="text-gray-400">Loading documents...</p>
          </div>
        </div>
      ) : filteredDocuments.length === 0 ? (
        <div className="glass-card p-12 text-center" data-testid="no-documents">
          <FolderOpen className="mx-auto mb-4 text-gray-400" size={64} />
          <h3 className="text-xl font-semibold mb-2">No documents found</h3>
          <p className="text-gray-400 mb-6">Upload documents to get started</p>
          <button
            onClick={() => setShowUploadModal(true)}
            className="btn-primary"
          >
            Upload Document
          </button>
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="documents-grid">
          {filteredDocuments.map((doc) => (
            <div
              key={doc.id}
              className="glass-card p-6 flex flex-col"
              data-testid={`document-card-${doc.id}`}
            >
              <div className="flex items-center justify-center mb-4 h-24">
                {getFileIcon(doc.fileType)}
              </div>
              <h3 className="font-semibold mb-2 line-clamp-2 text-center" data-testid="doc-filename">
                {doc.filename}
              </h3>
              <p className="text-xs text-gray-400 mb-2 text-center">
                {formatFileSize(doc.fileSize)}
              </p>
              <p className="text-xs text-gray-400 mb-2 text-center">
                {doc.uploadDate}
              </p>
              <span className="badge badge-blue text-xs text-center mb-4">{doc.category}</span>
              <div className="flex gap-2 mt-auto">
                <button
                  onClick={() => handleDownload(doc)}
                  className="flex-1 btn-secondary text-sm py-2 flex items-center justify-center gap-1"
                  data-testid={`download-btn-${doc.id}`}
                >
                  <Download size={14} />
                </button>
                <button
                  onClick={() => handleDelete(doc)}
                  className="flex-1 btn-secondary text-sm py-2 flex items-center justify-center gap-1 text-red-400 hover:bg-red-500/10"
                  data-testid={`delete-btn-${doc.id}`}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="glass-card overflow-hidden" data-testid="documents-list">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left p-4 text-sm font-medium text-gray-400">Name</th>
                <th className="text-left p-4 text-sm font-medium text-gray-400">Category</th>
                <th className="text-left p-4 text-sm font-medium text-gray-400">Size</th>
                <th className="text-left p-4 text-sm font-medium text-gray-400">Date</th>
                <th className="text-left p-4 text-sm font-medium text-gray-400">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredDocuments.map((doc) => (
                <tr key={doc.id} className="border-b border-white/10 hover:bg-white/5" data-testid={`document-row-${doc.id}`}>
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      {getFileIcon(doc.fileType)}
                      <span className="font-medium">{doc.filename}</span>
                    </div>
                  </td>
                  <td className="p-4">
                    <span className="badge badge-blue text-xs">{doc.category}</span>
                  </td>
                  <td className="p-4 text-sm text-gray-400">{formatFileSize(doc.fileSize)}</td>
                  <td className="p-4 text-sm text-gray-400">{doc.uploadDate}</td>
                  <td className="p-4">
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleDownload(doc)}
                        className="text-blue-400 hover:text-blue-300 transition-colors"
                        data-testid={`list-download-btn-${doc.id}`}
                      >
                        <Download size={18} />
                      </button>
                      <button
                        onClick={() => handleDelete(doc)}
                        className="text-red-400 hover:text-red-300 transition-colors"
                        data-testid={`list-delete-btn-${doc.id}`}
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <UploadModal
          onClose={() => setShowUploadModal(false)}
          onUpload={(newDoc) => {
            const updated = [newDoc, ...documents];
            setDocuments(updated);
            setShowUploadModal(false);
          }}
        />
      )}
    </div>
  );
};

const UploadModal = ({ onClose, onUpload }) => {
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [formData, setFormData] = useState({
    category: 'technical',
    description: '',
    tags: ''
  });

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setSelectedFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const getFileType = (filename) => {
    const ext = filename.split('.').pop().toLowerCase();
    if (['pdf'].includes(ext)) return 'pdf';
    if (['jpg', 'jpeg', 'png', 'gif'].includes(ext)) return 'image';
    if (['xls', 'xlsx'].includes(ext)) return 'excel';
    if (['doc', 'docx'].includes(ext)) return 'word';
    return 'other';
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      alert('Please select a file');
      return;
    }

    try {
      console.log('📤 UploadModal: Starting file upload...');
      const result = await uploadDocument(selectedFile, {
        category: formData.category,
        description: formData.description
      });
      
      if (result.success) {
        console.log('✅ UploadModal: Upload successful:', result.data);
        onUpload(result.data);
        toast.success('Document uploaded successfully');
      } else {
        console.error('❌ UploadModal: Upload failed:', result.error);
        toast.error('Failed to upload document: ' + result.error);
      }
    } catch (error) {
      console.error('❌ UploadModal: Unexpected upload error:', error);
      toast.error('Failed to upload document: ' + error.message);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose} data-testid="upload-modal">
      <div className="modal-content max-w-2xl" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="p-6 border-b border-white/10">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold">Upload Document</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors"
              data-testid="upload-modal-close-btn"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Drag & Drop Area */}
          <div
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            className={`border-2 border-dashed rounded-lg p-12 text-center transition-colors ${
              dragActive ? 'border-blue-500 bg-blue-500/10' : 'border-white/20 hover:border-blue-500/50'
            }`}
            data-testid="drop-zone"
          >
            <Upload className="mx-auto mb-4 text-gray-400" size={48} />
            <p className="text-lg mb-2">
              {selectedFile ? selectedFile.name : 'Drag files here to upload'}
            </p>
            <p className="text-sm text-gray-400 mb-4">or</p>
            <label className="btn-primary cursor-pointer inline-block">
              Browse Files
              <input
                type="file"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="file-input"
              />
            </label>
          </div>

          {/* Form Fields */}
          {selectedFile && (
            <>
              <div>
                <label className="block text-sm font-medium mb-2">Filename</label>
                <input
                  type="text"
                  value={selectedFile.name}
                  readOnly
                  className="w-full glass-input bg-white/5"
                  data-testid="filename-display"
                />
                <p className="text-xs text-gray-500 mt-1">Tip: Use format Category_Topic_Date.ext</p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Category</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full glass-input cursor-pointer"
                  data-testid="upload-category-select"
                >
                  <option value="datasheets">Datasheets</option>
                  <option value="bills">Bills</option>
                  <option value="technical">Technical</option>
                  <option value="reports">Reports</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-bold mb-1">Description</label>
                <p className="text-xs text-gray-400 mb-2">(AI uses this to find documents - be detailed)</p>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full glass-input resize-none"
                  rows="5"
                  placeholder="Enter detailed document description, including key topics, components, dates, and relevant technical information..."
                  data-testid="upload-description-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Tags</label>
                <input
                  type="text"
                  value={formData.tags}
                  onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                  className="w-full glass-input"
                  placeholder="Enter tags separated by commas"
                  data-testid="upload-tags-input"
                />
              </div>
            </>
          )}
        </div>

        {/* Actions */}
        <div className="p-6 border-t border-white/10 flex gap-3 justify-end">
          <button onClick={onClose} className="btn-secondary" data-testid="upload-cancel-btn">
            Cancel
          </button>
          <button
            onClick={handleUpload}
            className="btn-primary"
            disabled={!selectedFile}
            data-testid="upload-submit-btn"
          >
            Upload
          </button>
        </div>
      </div>
    </div>
  );
};

export default Documents;
